import React, { useState } from 'react';
import { Phone } from 'lucide-react';
import { Section, Card } from '../shared/primitives';
import { Segmented, StepRow, SourceRow, ExpandableGroups } from '../shared/protocolBits';
import {
  BELMONT_SOURCES, BELMONT_SUPPORT, SCREEN_LEGEND, INFUSING,
  ALARMS, QUIRKS, FLUIDS, SETUP_NOTE, SETUP,
} from '../../../data/hospitals/thiBelmont';

const ACCENT = '#9F8D70';
const TABS = [
  { id: 'infusing', label: 'Infusing' },
  { id: 'alarms', label: 'Alarms' },
  { id: 'fluids', label: 'Fluids' },
  { id: 'setup', label: 'Setup' },
];

/** Numbered callout badge used on the schematic and in its legend */
const Callout = ({ n, className = '' }) => (
  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold text-white ${className}`} style={{ backgroundColor: ACCENT }}>{n}</span>
);

/**
 * The RI-2 Infuse screen, redrawn from the operator's manual: a status grid on top
 * and the key row along the bottom. Callout numbers match SCREEN_LEGEND.
 */
const InfuseScreen = () => {
  const cell = 'relative px-2.5 py-2 border-gray-400 dark:border-gray-500 text-[11px] leading-tight text-gray-800 dark:text-gray-100';
  const key = 'relative flex items-center justify-center text-center text-[10px] font-semibold leading-tight border-gray-400 dark:border-gray-500';
  return (
    <div className="bg-gray-200 dark:bg-gray-900 rounded-xl p-3">
      <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-400 dark:border-gray-500 rounded-md overflow-hidden font-mono">
        <div className="grid grid-cols-2">
          <div className={`${cell} border-b border-r`}>
            <Callout n={1} className="absolute -top-0 -left-0 translate-x-0.5 translate-y-0.5" />
            <div className="pl-6">SET RATE = 500 <span className="text-[9px]">mL/min</span></div>
          </div>
          <div className={`${cell} border-b font-semibold`}>INFUSING</div>
          <div className={`${cell} border-b border-r`}><div className="pl-6">ACTUAL = 500 <span className="text-[9px]">mL/min</span></div></div>
          <div className={`${cell} border-b`}>
            <Callout n={3} className="absolute top-0 right-0 -translate-x-0.5 translate-y-0.5" />
            T = 37.3 °C
          </div>
          <div className={`${cell} border-b border-r`}>
            <Callout n={2} className="absolute top-0 left-0 translate-x-0.5 translate-y-0.5" />
            <div className="pl-6">VOL = 16.2 L</div>
          </div>
          <div className={`${cell} border-b`}>
            <Callout n={4} className="absolute top-0 right-0 -translate-x-0.5 translate-y-0.5" />
            P = 125 mmHg
          </div>
        </div>
        <div className="grid grid-cols-4 h-[72px]">
          <div className="grid grid-rows-2 border-r border-gray-400 dark:border-gray-500">
            <div className={`${key} border-b`}>
              <Callout n={5} className="absolute top-0.5 left-0.5" />
              RATE ▲
            </div>
            <div className={key}>RATE ▼</div>
          </div>
          <div className={`${key} border-r bg-gray-900 text-white dark:bg-black`}>
            <Callout n={6} className="absolute top-0.5 left-0.5" />
            500<br />mL/min
          </div>
          <div className="grid grid-rows-2 border-r border-gray-400 dark:border-gray-500">
            <div className={`${key} border-b`}>
              <Callout n={7} className="absolute top-0.5 left-0.5" />
              BOLUS<br />200 mL
            </div>
            <div className={key}>
              <Callout n={8} className="absolute top-0.5 left-0.5" />
              RECIRC
            </div>
          </div>
          <div className={key}>
            <Callout n={9} className="absolute top-0.5 left-0.5" />
            STOP
          </div>
        </div>
      </div>
    </div>
  );
};

const Steps = ({ steps }) => steps.map((step, i) => (
  <div key={step} className="px-4 py-2.5 flex gap-3">
    <span className="w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-bold flex-shrink-0" style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}>{i + 1}</span>
    <span className="text-sm text-gray-900 dark:text-white leading-snug pt-0.5">{step}</span>
  </div>
));

/** Belmont Rapid Infuser RI-2 for the clinician at the bedside */
const BelmontGuide = () => {
  const [tab, setTab] = useState('infusing');

  return (
    <div className="space-y-6">
      <Segmented items={TABS} value={tab} onChange={setTab} />

      {tab === 'infusing' && (
        <>
          <Section title="Display and controls">
            <Card>
              <div className="p-3"><InfuseScreen /></div>
              <div className="px-4 py-2 grid grid-cols-1 gap-y-2">
                {SCREEN_LEGEND.map((l) => (
                  <div key={l.n} className="flex items-start gap-2.5 text-sm">
                    <Callout n={l.n} className="mt-0.5 flex-shrink-0" />
                    <span className="leading-snug">
                      <span className="font-medium text-gray-900 dark:text-white">{l.label}</span>
                      <span className="text-gray-500 dark:text-gray-400"> · {l.text}</span>
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </Section>

          {INFUSING.map((g) => (
            <Section key={g.title} title={g.title}>
              <Card><StepRow items={g.items} /></Card>
            </Section>
          ))}

        </>
      )}

      {tab === 'alarms' && (
        <>
          <p className="px-1 text-sm text-gray-600 dark:text-gray-300 leading-snug">
            Any alarm: pump and heat stop, the valve swings to recirculate, the light goes red. MUTE silences it; it clears when the cause does.
          </p>
          <ExpandableGroups
            groups={ALARMS.map((a) => ({
              id: a.title,
              title: a.title,
              danger: a.danger,
              content: (
                <div className={`px-4 py-3 ${a.danger ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-snug">{a.what}</p>
                  <p className={`mt-1.5 text-sm leading-snug ${a.danger ? 'text-red-900 dark:text-red-100 font-medium' : 'text-gray-900 dark:text-white'}`}>{a.fix}</p>
                </div>
              ),
            }))}
          />
          <Section title="Not an alarm, still a problem">
            <Card>
              {QUIRKS.map((q) => (
                <div key={q.problem} className="px-4 py-3">
                  <div className="text-[15px] font-medium text-gray-900 dark:text-white">{q.problem}</div>
                  <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 leading-snug">{q.fix}</p>
                </div>
              ))}
            </Card>
          </Section>
        </>
      )}

      {tab === 'fluids' && (
        <>
          <Section title="Yes">
            <Card><StepRow items={FLUIDS.yes} /></Card>
          </Section>
          <Section title="No">
            <Card>
              <StepRow items={FLUIDS.no} />
              <p className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 leading-snug">{FLUIDS.calcium}</p>
            </Card>
          </Section>
        </>
      )}

      {tab === 'setup' && (
        <>
          <p className="px-1 text-sm text-gray-600 dark:text-gray-300">{SETUP_NOTE}</p>
          {SETUP.map((g) => (
            <Section key={g.title} title={g.title}>
              <Card><Steps steps={g.steps} /></Card>
            </Section>
          ))}
        </>
      )}

      <Section title="Resources">
        <div className="space-y-3">
          {BELMONT_SOURCES.map((s) => (
            <SourceRow key={s.title} href={s.href} title={s.title} accent={ACCENT} kind={s.kind} />
          ))}
          <Card>
            <a href={`tel:${BELMONT_SUPPORT.phone}`} onClick={() => {}} className="px-4 py-3 flex items-center gap-3">
              <Phone size={18} style={{ color: ACCENT }} />
              <span className="flex-1 text-[15px] font-medium text-gray-900 dark:text-white">{BELMONT_SUPPORT.label}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{BELMONT_SUPPORT.phone}</span>
            </a>
          </Card>
        </div>
      </Section>
    </div>
  );
};

export default BelmontGuide;
